import { useEffect } from 'react';

const TOOLTIP_ATTRS = ['title', 'data-title', 'data-original-title', 'data-bs-original-title'];

const stripFromElement = (element) => {
    if (!element || !element.removeAttribute) return;
    TOOLTIP_ATTRS.forEach((attr) => element.removeAttribute(attr));
    element.querySelectorAll?.('svg title').forEach((node) => node.remove());
};

const stripFromSubtree = (root = document) => {
    const selector = TOOLTIP_ATTRS.map((attr) => `[${attr}]`).join(', ');
    root.querySelectorAll?.(selector).forEach(stripFromElement);
    root.querySelectorAll?.('svg title').forEach((node) => node.remove());
};

const stripFromAncestors = (start) => {
    let element = start;
    while (element && element !== document.body) {
        stripFromElement(element);
        element = element.parentElement;
    }
};

/**
 * Continuously strips tooltip attributes (`title`, `data-title`,
 * `data-original-title`, `data-bs-original-title`) and SVG <title> elements
 * from the document. Necessary because react-bootstrap and ant-design
 * components inject native tooltips that cannot be disabled declaratively.
 */
export const useTooltipSuppression = () => {
    useEffect(() => {
        stripFromSubtree();

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes') {
                    stripFromAncestors(mutation.target);
                }
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        stripFromSubtree(node);
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: TOOLTIP_ATTRS,
        });

        const onEvent = (event) => stripFromAncestors(event.target);
        document.addEventListener('mouseover', onEvent, true);
        document.addEventListener('focusin', onEvent, true);

        return () => {
            observer.disconnect();
            document.removeEventListener('mouseover', onEvent, true);
            document.removeEventListener('focusin', onEvent, true);
        };
    }, []);
};
